/* eslint-disable no-console */
/* eslint-disable no-unused-vars */
/* eslint-disable consistent-return */
/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { notification } from 'antd';
import { normalize } from 'normalizr';

import request from '@/utils/axios';

import savedSearchSchema from './schema/index';

const initialState = {
  savedSearches: {
    isFetchingCompliassistSavedSearches: false,
    savedSearches: {},
  },
  savedSearch: {
    isSavingSearch: false,
    isDeletingSearch: false,
    selectedSearch: {
      searchId: null,
      personId: null,
      fullName: null,
      searchDescription: null,
      includeOnHome: null,
      searchText: null,
      states: [],
      categories: [],
      charts: [],
      dateStart: null,
      dateEnd: null,
      page: null,
      pageSize: null,
      sortField: null,
      sortOrder: null,
    },
  },
  searchOptions: {
    isLoadingOptions: true,
    categories: [],
    charts: [],
    states: [],
    searchText: '',
  },
  searchFiltersValues: {
    categories: null,
    charts: [],
    states: [],
    searchText: '',
    dateStart: null,
    dateEnd: null,
  },
  searchResults: {
    isFetchingCharts: false,
    groupByChart: true,
    searchResults: {
      charts: [],
      count: 0,
    },
    currentPage: 1,
  },
  stateChart: {
    isFetchingStateChart: true,
    data: {
      ChartJurId: null,
      ChartId: null,
      ChartTitle: '',
      StateCode: '',
      StateDescription: '',
      StateNotes: '',
      Topics: [],
    },
    error: false,
  },
  chartDetail: {
    isFetchingChartData: true,
    chartData: {
      ChartDescription: '',
      ChartNotes: '',
      ChartTitle: '',
      Contact: {
        PersonId: null,
        FirstName: '',
        MiddleName: '',
        LastName: '',
        FullName: '',
        EmailAddress: '',
        Phone: '',
      },
    },
  },
  chartDescriptions: {
    isLoadingChartDescriptions: false,
    chartDescriptions: [],
  },
};

export const requestSavedSearches = createAsyncThunk(
  'compliance/requestSavedSearches',
  async ({ personId, includeOnHome }, { rejectWithValue }) => {
    try {
      const params = { personId };

      if (includeOnHome === 'Y') {
        params.includeOnHome = includeOnHome;
      }

      const response = await request({
        url: '/compliance/savedsearches',
        baseURL: process.env.V2_API_ROOT,
        method: 'GET',
        params,
        triggerLoad: false,
      });
      
      const {
        data
      } = response;

      if (Array.isArray(data) && data[0].searchId) {
        const normalizedData = normalize(data, [savedSearchSchema]);
        const {
          entities: { searches },
        } = normalizedData;

        return searches;
      }

      return {};
    } catch (err) {
      console.warn(err);
      return rejectWithValue(err);
    }
  }
);

export const requestSearchOptions = createAsyncThunk(
  'compliance/requestSearchOptions',
  async (_props, { rejectWithValue }) => {
    try {
      const response = await request({
        url: 'shared/GetLOVs',
        baseURL: process.env.V2_API_ROOT,
        method: 'POST',
        data: ['compliancecategories', 'compliancecharts', 'compliancestates'],
      });

      const { data } = response;

      const categories = data.find(d => d.compliancecategories)?.compliancecategories ?? [];
      const charts = data.find(d => d.compliancecharts)?.compliancecharts ?? [];
      const states = data.find(d => d.compliancestates)?.compliancestates ?? [];

      return {
        categories: categories.map(c => ({
          categoryId: c.categoryId,
          categoryDescription: c.category,
        })),
        charts: charts.map(c => ({
          chartId: c.chartId,
          chartTitle: c.chartTitle,
          chartDescription: c.chartDescription || c.chartTitle,
          categories: (c.categories || []).map(cat => ({
            categoryId: cat.categoryId,
            categoryDescription: cat.category,
          })),
        })),
        states: states.map(s => ({
          stateCode: s.stateCode,
          stateDescription: s.stateDescription,
        })),
        searchText: '',
      };
    } catch (err) {
      console.warn(err);
      return rejectWithValue(err);
    }
  }
);

export const requestCharts = createAsyncThunk(
  'compliance/requestCharts',
  async ({ payload }, { rejectWithValue }) => {
    try {
      const response = await request({
        method: 'POST',
        url: '/compliance/search',
        baseURL: process.env.V2_API_ROOT,
        data: payload,
        triggerLoad: false,
      });

      const { results, count } = response.data;

      return {
        charts: results ||  [],
        count: count || 0,
      };
    } catch (err) {
      console.warn(err);
      return rejectWithValue(err);
    }
  }
);

export const requestStateChart = createAsyncThunk(
  'compliance/requestStateChart',
  async ({ chartId, stateCode }, { rejectWithValue }) => {
    try {
      const response = await request({
        url: '/compliassist/chartstate',
        method: 'GET',
        params: {
          chartId,
          stateCode,
        },
      });

      const { data } = response;

      return data;
    } catch (err) {
      console.warn(err);
      return rejectWithValue(err);
    }
  }
);

export const setSearchandFetchResults = createAsyncThunk(
  'compliance/setSearchandFetchResults',
  async ({ searchId, personId }, { rejectWithValue }) => {
    try {
      const response = await request({
        url: '/compliance/savedsearches',
        baseURL: process.env.V2_API_ROOT,
        method: 'GET',
        params: {
          personId,
        },
        triggerLoad: false,
      });

      const { data } = response;

      const searches = Array.isArray(data) ? data : data?.Searches || [];

      const selectedSearch = searches.find(
        search =>
          Number(search.searchId || search.SearchId) === Number(searchId)
      );

      if (!selectedSearch) {
        return {
          searchFilters: {
            categories: null,
            charts: [],
            states: [],
            searchText: '',
            dateStart: null,
            dateEnd: null,
          },
          charts: [],
          count: 0,
        };
      }

      const chartResults = await request({
        method: 'POST',
        url: '/compliance/search',
        baseURL: process.env.V2_API_ROOT,
        data: selectedSearch,
      });

      const searchFiltersPayload =
        selectedSearch.fieldOptions ||
        selectedSearch.FieldOptions ||
        selectedSearch;

      const formattedPayload = {
        searchText:
          searchFiltersPayload.searchText ||
          searchFiltersPayload.SearchText ||
          '',
        categories:
          Array.isArray(searchFiltersPayload.categories) &&
          searchFiltersPayload.categories.length
            ? searchFiltersPayload.categories.map(c => c.categoryId || c.CategoryId)[0]
            : Array.isArray(searchFiltersPayload.Categories) &&
                searchFiltersPayload.Categories.length
              ? searchFiltersPayload.Categories.map(c => c.CategoryId || c.categoryId)[0]
              : null,
        charts:
          Array.isArray(searchFiltersPayload.charts) &&
          searchFiltersPayload.charts.length
            ? searchFiltersPayload.charts.map(c => c.chartId || c.ChartId)
            : Array.isArray(searchFiltersPayload.Charts) &&
                searchFiltersPayload.Charts.length
              ? searchFiltersPayload.Charts.map(c => c.ChartId || c.chartId)
              : [],
        states:
          Array.isArray(searchFiltersPayload.states) &&
          searchFiltersPayload.states.length
            ? searchFiltersPayload.states.map(s => s.stateCode || s.StateCode)
            : Array.isArray(searchFiltersPayload.States) &&
                searchFiltersPayload.States.length
              ? searchFiltersPayload.States.map(s => s.StateCode || s.stateCode)
              : [],
        dateStart:
          searchFiltersPayload.dateStart ||
          searchFiltersPayload.DateStart ||
          null,
        dateEnd:
          searchFiltersPayload.dateEnd ||
          searchFiltersPayload.DateEnd ||
          null,
      };

      const { results, charts, count } = chartResults.data;

      return {
        searchFilters: formattedPayload,
        charts: results || charts || [],
        count: count || 0,
      };
    } catch (err) {
      console.warn(err);
      return rejectWithValue(err);
    }
  }
);

export const chartDetailAction = createAsyncThunk(
  'compliance/chartDetailAction',
  async ({ chartId }, { rejectWithValue }) => {
    try {
      const response = await request({
        url: '/compliassist/chart',
        method: 'GET',
        params: {
          chartId,
        },
        triggerLoad: false,
      });

      const { data } = response;

      return data;
    } catch (err) {
      console.warn(err);
      return rejectWithValue(err);
    }
  }
);

export const requestChartDescriptions = createAsyncThunk(
  'compliance/requestChartDescriptions',
  async (_props, { rejectWithValue }) => {
    try {
      const response = await request({
        url: '/shared/GetLOVs',
        method: 'POST',
        baseURL: process.env.V2_API_ROOT,
        data: ['compliancecharts'],
        triggerLoad: false,
      });

      const { data } = response;

      const charts = data.find(d => d.compliancecharts)?.compliancecharts ?? [];

      return charts.map(c => ({
        chartId: c.chartId,
        chartTitle: c.chartTitle,
        chartDescription: c.chartDescription || c.chartTitle,
      }));
    } catch (err) {
      console.warn(err);
      return rejectWithValue(err);
    }
  }
);

export const setSelectedSearch = createAsyncThunk(
  'compliance/setSelectedSearch',
  async (id, { rejectWithValue }) => {
    try {
      return id;
    } catch (err) {
      console.warn(err);
      return rejectWithValue(err);
    }
  }
);

export const saveCompliassistSearch = createAsyncThunk(
  'saveCompliassistSearch',
  async ({ payload, successCallback }, { rejectWithValue, getState }) => {
    try {
      const currentState = getState().compliance;
      const {
        savedSearches: { savedSearches },
      } = currentState;

      const _savedSearches = { ...savedSearches };

      const existingSearchId = payload.compChartSearchId || payload.searchId;

      if (existingSearchId) {
        _savedSearches[existingSearchId] = payload;
      } else {
        _savedSearches.new = payload;
      }

      const response = await request({
        method: 'POST',
        url: '/compliance/savesearch',
        baseURL: process.env.V2_API_ROOT,
        data: payload,
      });

      const { status, data } = response;

      const savedSearchId = data?.data?.compChartSearchId;

      if (status === 200 && savedSearchId) {
        if (_savedSearches.new) {
          delete _savedSearches.new;

          _savedSearches[savedSearchId] = {
            ...payload,
            compChartSearchId: savedSearchId,
            searchId: savedSearchId,
          };
        } else {
          _savedSearches[existingSearchId] = {
            ...payload,
            compChartSearchId: savedSearchId,
            searchId: savedSearchId,
          };
        }

        notification.success({
          message: 'Saved Search',
          description: data?.statusMessage || 'Search successfully saved.',
        });

        if (successCallback) {
          successCallback(savedSearchId);
        }
        return _savedSearches;
      }

      return rejectWithValue(data);
    } catch (err) {
      notification.error({
        message: 'Saved Search',
        description: 'Search unsuccessfully saved.',
      });

      console.warn(err);

      return rejectWithValue(err);
    }
  }
);

export const deleteCompliassistSavedSearch = createAsyncThunk(
  'compliance/deleteCompliassistSavedSearch',
  async (searchId, { rejectWithValue, getState }) => {
    try {
      const currentState = getState().compliance;
      const {
        savedSearches: { savedSearches },
      } = currentState;

      const _savedSearches = { ...savedSearches };
      delete _savedSearches[searchId];

      const response = await request({
        url: '/compliance/deletesearch',
        baseURL: process.env.V2_API_ROOT,
        method: 'DELETE',
        params: {
          searchId,
        },
        triggerLoad: false,
      });

      const { status } = response;

      if (status === 200) {
        notification.success({
          message: 'Saved Search',
          description: 'Search successfully deleted.',
        });

        return _savedSearches;
      }

      notification.error({
        message: 'Saved Search',
        description: 'Search unsuccessfully deleted.',
      });

      return savedSearches;
    } catch (err) {
      console.warn(err);
      return rejectWithValue(err);
    }
  }
);

export const resetResults = createAsyncThunk(
  'compliance/resetResults',
  async () => {
    return [];
  }
);

const complianceSlice = createSlice({
  name: 'compliance',
  initialState,
  reducers: {
    setCurrentPage(state, action) {
      state.searchResults.currentPage = action.payload;
    },
    setGroupBy(state, action) {
      state.searchResults.groupByChart = action.payload;
    },
    setSearchFilters(state, action) {
      state.searchFiltersValues = action.payload;
    },
    resetSearchFilters(state) {
      state.searchFiltersValues = {
        categories: null,
        charts: [],
        states: [],
        searchText: '',
        dateStart: null,
        dateEnd: null,
      };
    },
    updateSearchFilters(state, action) {
      state.searchFiltersValues[action.payload.field] = action.payload.value;
    },
  },
  extraReducers: builder => {
    builder.addCase(requestSavedSearches.pending, state => {
      state.savedSearches.isFetchingCompliassistSavedSearches = true;
    });

    builder.addCase(requestSavedSearches.fulfilled, (state, action) => {
      state.savedSearches.savedSearches = action.payload;
      state.savedSearches.isFetchingCompliassistSavedSearches = false;
    });

    builder.addCase(requestSavedSearches.rejected, state => {
      state.savedSearches.isFetchingCompliassistSavedSearches = false;
    });

    builder.addCase(requestSearchOptions.pending, state => {
      state.searchOptions.isLoadingOptions = true;
    });

    builder.addCase(requestSearchOptions.fulfilled, (state, action) => {
      state.searchOptions.isLoadingOptions = false;
      state.searchOptions.categories = action.payload.categories;
      state.searchOptions.charts = action.payload.charts;
      state.searchOptions.states = action.payload.states;
      state.searchOptions.searchText = action.payload.searchText;
    });

    builder.addCase(requestSearchOptions.rejected, state => {
      state.searchOptions.isLoadingOptions = false;
    });

    builder.addCase(requestCharts.pending, state => {
      state.searchResults.isFetchingCharts = true;
    });

    builder.addCase(requestCharts.fulfilled, (state, action) => {
      state.searchResults.searchResults = action.payload || {
        charts: [],
        count: 0,
      };
      state.searchResults.isFetchingCharts = false;
    });

    builder.addCase(requestCharts.rejected, state => {
      state.searchResults.isFetchingCharts = false;
    });

    builder.addCase(requestStateChart.pending, state => {
      state.stateChart.isFetchingStateChart = true;
    });

    builder.addCase(requestStateChart.fulfilled, (state, action) => {
      state.stateChart.isFetchingStateChart = false;
      state.stateChart.data = action.payload;
    });

    builder.addCase(requestStateChart.rejected, state => {
      state.stateChart.isFetchingStateChart = false;
      state.stateChart.error = true;
    });

    builder.addCase(chartDetailAction.pending, state => {
      state.chartDetail.isFetchingChartData = true;
    });

    builder.addCase(chartDetailAction.fulfilled, (state, action) => {
      state.chartDetail.isFetchingChartData = false;
      state.chartDetail.chartData = action.payload;
    });

    builder.addCase(chartDetailAction.rejected, state => {
      state.chartDetail.isFetchingChartData = false;
    });

    builder.addCase(requestChartDescriptions.pending, state => {
      state.chartDescriptions.isLoadingChartDescriptions = true;
    });

    builder.addCase(requestChartDescriptions.fulfilled, (state, action) => {
      state.chartDescriptions.chartDescriptions = action.payload;
      state.chartDescriptions.isLoadingChartDescriptions = false;
    });

    builder.addCase(requestChartDescriptions.rejected, state => {
      state.chartDescriptions.isLoadingChartDescriptions = false;
    });

    builder.addCase(setSearchandFetchResults.pending, state => {
      state.savedSearches.isFetchingCompliassistSavedSearches = true;
    });

    builder.addCase(setSearchandFetchResults.fulfilled, (state, action) => {
      state.savedSearches.isFetchingCompliassistSavedSearches = false;
      state.searchResults.searchResults = {
        charts: action.payload.charts,
        count: action.payload.count,
      };
      state.searchFiltersValues = action.payload.searchFilters;
    });

    builder.addCase(setSearchandFetchResults.rejected, state => {
      state.savedSearches.isFetchingCompliassistSavedSearches = false;
    });

    builder.addCase(setSelectedSearch.fulfilled, (state, action) => {
      state.savedSearch.selectedSearch.searchId = action.payload;
      state.savedSearch.selectedSearch.SearchId = action.payload;
    });

    builder.addCase(saveCompliassistSearch.pending, state => {
      state.savedSearches.isFetchingCompliassistSavedSearches = true;
    });

    builder.addCase(saveCompliassistSearch.fulfilled, (state, action) => {
      state.savedSearches.savedSearches = action.payload;
      state.savedSearches.isFetchingCompliassistSavedSearches = false;
    });

    builder.addCase(saveCompliassistSearch.rejected, state => {
      state.savedSearches.isFetchingCompliassistSavedSearches = false;
    });

    builder.addCase(deleteCompliassistSavedSearch.fulfilled, (state, action) => {
      state.savedSearch.savedSearch = action.payload;
    });

    builder.addCase(resetResults.fulfilled, state => {
      state.searchResults.searchResults = {
        charts: [],
        count: 0,
      };
      state.searchResults.isFetchingCharts = false;
    });
  },
});

export const {
  setCurrentPage,
  setGroupBy,
  setSearchFilters,
  resetSearchFilters,
  updateSearchFilters,
} = complianceSlice.actions;

export default complianceSlice.reducer;